import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TablePagination,
  Skeleton,
} from "@mui/material";

import { ReactComponent as ExportDownloadIcon } from "../../../assets/images/export-download.svg";
import AddExportDataPopUp from "./AddExportDataPopup";
import { DATA_EXPORT_REPORTS_MOCK } from "../../../constants/dataExportReports";
import "./style.css";

const ROW_HEIGHT_PX = 63;
const BORDER_COLOR = "#E5E9EC";

const reportNameCellSx = {
  paddingLeft: 0,
  color: "#061522 !important",
  fontFamily: "Inter",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500 !important",
  lineHeight: "18px",
};

function formatGeneratedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatStatus(status) {
  if (!status || typeof status !== "string") return "—";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * @param {object} props
 * @param {boolean} [props.showExportButton]
 * @param {React.ReactNode} [props.headerActions] — optional controls next to the title (e.g. group select)
 * @param {object} [props.remoteExports] — when set, table uses API rows + server pagination (analytics).
 * @param {Array<object>} props.remoteExports.items
 * @param {number} props.remoteExports.total
 * @param {boolean} props.remoteExports.loading
 * @param {string|null} [props.remoteExports.error]
 * @param {number} props.remoteExports.page — API page (1-based)
 * @param {number} props.remoteExports.rowsPerPage
 * @param {function} props.remoteExports.onPageChange — (event, newZeroBasedPage) => void
 */
export default function DataExport({ showExportButton = true, remoteExports, headerActions }) {
  const [addExportDataPopUpOpen, setAddExportDataPopUpOpen] = useState(false);
  const useRemote = remoteExports != null;

  const handleDownloadMock = (id) => {
    console.log("Download clicked for id:", id);
  };

  const handleDownloadRemote = (row) => {
    const url = row.download_url || row.file_url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleChangePage = (event, newPage) => {
    if (useRemote && remoteExports.onPageChange) {
      remoteExports.onPageChange(event, newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (useRemote && remoteExports.onRowsPerPageChange) {
      remoteExports.onRowsPerPageChange(event);
    }
  };

  const tablePage = useRemote ? Math.max(0, (remoteExports.page || 1) - 1) : 0;
  const rowsPerPage = useRemote ? remoteExports.rowsPerPage || 10 : 10;
  const totalCount = useRemote ? remoteExports.total || 0 : 0;
  const skeletonRowCount = useRemote ? rowsPerPage : 10;

  return (
    <Box
      className="Data-export-main-container"
      sx={{
        ...(useRemote && {
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "29px",
          ...(useRemote && { flexShrink: 0 }),
        }}
      >
        <Typography className="data-export-heading">Data Export</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          {headerActions}
          {showExportButton ? (
            <Button
              variant="outlined"
              className="export-btn"
              size="small"
              onClick={() => setAddExportDataPopUpOpen(true)}
            >
              Export Data
            </Button>
          ) : null}
        </Box>
      </Box>

      {useRemote && remoteExports.error ? (
        <Typography
          sx={{
            color: "#d32f2f",
            mb: 2,
            fontSize: "14px",
            ...(useRemote && { flexShrink: 0 }),
          }}
        >
          {remoteExports.error}
        </Typography>
      ) : null}

      <Box
        sx={{
          ...(useRemote && {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }),
        }}
      >
      <TableContainer
        sx={{
          ...(useRemote && {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }),
        }}
      >
        <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  color: "#405261",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                  paddingBottom: "12px",
                  paddingTop: 0,
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  height: "auto",
                },
              }}
            >
              <TableCell
                style={{ paddingLeft: 0 }}
                sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}
              >
                Report Name
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Period
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Generated
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Format
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Status
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Size
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  width: 48,
                  paddingRight: 0,
                }}
              />
            </TableRow>
          </TableHead>

          <TableBody>
            {useRemote && remoteExports.loading
              ? Array.from({ length: skeletonRowCount }).map((_, i) => (
                  <TableRow
                    key={`export-skeleton-${i}`}
                    sx={{
                      "& td": {
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                        height: ROW_HEIGHT_PX,
                        paddingTop: 0,
                        paddingBottom: 0,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableCell sx={{ paddingLeft: 0 }}>
                      <Skeleton variant="text" width="78%" height={20} sx={{ maxWidth: 280 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="88%" height={18} sx={{ maxWidth: 200 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={112} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={36} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: "4px" }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={52} height={20} />
                    </TableCell>
                    <TableCell align="right" sx={{ paddingRight: 0, width: 48 }}>
                      <Skeleton variant="circular" width={28} height={28} sx={{ ml: "auto" }} />
                    </TableCell>
                  </TableRow>
                ))
              : useRemote && (!remoteExports.items || remoteExports.items.length === 0) ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ border: "none", py: 6 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#8C8C8C", textAlign: "center", width: "100%" }}
                  >
                    No exports available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : useRemote
              ? remoteExports.items.map((row) => (
                  <TableRow
                    key={row._id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#F9FAFA" },
                      "& td": {
                        color: "#405261",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        height: ROW_HEIGHT_PX,
                        paddingTop: 0,
                        paddingBottom: 0,
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                        verticalAlign: "middle",
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    <TableCell sx={reportNameCellSx}>
                      {row.report_title || row.export_type || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#93A1B0",
                        fontSize: "14px",
                        fontWeight: 400,
                        maxWidth: 220,
                      }}
                    >
                      {row.report_subtitle || "—"}
                    </TableCell>
                    <TableCell sx={{ color: "#405261" }}>
                      {formatGeneratedAt(row.generated_at || row.completed_at)}
                    </TableCell>
                    <TableCell sx={{ textTransform: "uppercase" }}>
                      {row.file_format || "—"}
                    </TableCell>
                    <TableCell>{formatStatus(row.status)}</TableCell>
                    <TableCell>{row.file_size_human || "—"}</TableCell>
                    <TableCell align="right" sx={{ paddingRight: 0, width: 48 }}>
                      <IconButton
                        onClick={() => handleDownloadRemote(row)}
                        aria-label="Download export"
                        disabled={!(row.download_url || row.file_url) || row.status !== "ready"}
                        sx={{
                          padding: "4px",
                          "&:hover": { backgroundColor: "rgba(64, 82, 97, 0.08)" },
                        }}
                      >
                        <ExportDownloadIcon width={24} height={24} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              : DATA_EXPORT_REPORTS_MOCK.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#F9FAFA" },
                      "& td": {
                        color: "#405261",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        height: ROW_HEIGHT_PX,
                        paddingTop: 0,
                        paddingBottom: 0,
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                        verticalAlign: "middle",
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    <TableCell sx={reportNameCellSx}>{row.reportName}</TableCell>
                    <TableCell sx={{ color: "#93A1B0", fontSize: "14px" }}>—</TableCell>
                    <TableCell sx={{ color: "#405261" }}>{row.generated}</TableCell>
                    <TableCell sx={{ textTransform: "uppercase" }}>{row.format}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.size}</TableCell>
                    <TableCell align="right" sx={{ paddingRight: 0, width: 48 }}>
                      <IconButton
                        onClick={() => handleDownloadMock(row.id)}
                        aria-label="Download export"
                        sx={{
                          padding: "4px",
                          "&:hover": { backgroundColor: "rgba(64, 82, 97, 0.08)" },
                        }}
                      >
                        <ExportDownloadIcon width={24} height={24} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {useRemote && totalCount > rowsPerPage ? (
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            background: "#fff",
            borderTop: "1px solid #E5E9EC",
            padding: "16px 0px",
            position: "sticky",
            bottom: 0,
            left: 0,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={tablePage}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to }) => (
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
              >
                Exports {from}-{to}
              </Typography>
            )}
            ActionsComponent={(props) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginLeft: "auto",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  onClick={(e) => props.onPageChange(e, props.page - 1)}
                  disabled={props.page === 0 || remoteExports.loading}
                  sx={{
                    width: { xs: "100px", sm: "128px" },
                    height: "40px",
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",
                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page + 1)}
                  disabled={
                    remoteExports.loading ||
                    props.page >= Math.ceil(props.count / rowsPerPage) - 1
                  }
                  sx={{
                    width: { xs: "80px", sm: "88px" },
                    height: "40px",
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFFFFF",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",
                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            sx={{
              ".MuiTablePagination-toolbar": {
                padding: 0,
                minHeight: "unset",
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              },
              ".MuiTablePagination-spacer": { display: "none" },
              ".MuiTablePagination-selectLabel": { display: "none" },
              ".MuiTablePagination-select": { display: "none" },
              ".MuiTablePagination-selectIcon": { display: "none" },
            }}
          />
        </Box>
      ) : null}
      </Box>

      {showExportButton && addExportDataPopUpOpen && (
        <AddExportDataPopUp
          open={addExportDataPopUpOpen}
          onClose={() => setAddExportDataPopUpOpen(false)}
        />
      )}
    </Box>
  );
}
